import { useEffect, useId, useState } from 'react';
import { Box, Paragraph, TextButton } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { getUndisclosedHost, normalizeUrl } from '../../utils/urlUtils';

export interface TruncatedUrlDisplayProps {
  /** The URL to display with potential truncation */
  url: string;
  /** The text to display (if different from url) */
  displayText?: string;
  /**
   * Renders the host `url` opens beneath the link whenever the displayed text spells out a different
   * one. Opt-in, because it only tells a reader anything when the displayed text is unparsed
   * sender-supplied text rather than a normalized URL.
   */
  shouldShowDestinationHost?: boolean;
  /** Callback when the URL is clicked */
  onClick: () => void;
  /** Maximum number of lines before truncation (default: 5) */
  maxLines?: number;
  /** Average characters per line for estimation (default: 50) */
  charsPerLine?: number;
  /** Custom styling for the container */
  containerCss?: Record<string, unknown>;
  /** Custom styling for the URL button */
  urlButtonCss?: Record<string, unknown>;
}

/**
 * A reusable component that displays a URL with automatic truncation after a specified number of lines.
 * Shows expand/collapse controls only when the URL exceeds the line limit.
 *
 * @example
 * ```tsx
 * <TruncatedUrlDisplay
 *   url="https://very-long-url.com/..."
 *   onClick={() => emitLinkClickedEvent()}
 *   maxLines={3}
 * />
 * ```
 */
export function TruncatedUrlDisplay({
  url,
  displayText,
  shouldShowDestinationHost = false,
  onClick,
  maxLines = 5,
  charsPerLine = 50,
  containerCss = {},
  urlButtonCss = {},
}: TruncatedUrlDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowControls, setShouldShowControls] = useState(false);
  const destinationHostId = useId();

  const textToDisplay = displayText || url;
  const href = normalizeUrl(url);
  // Compare against `href`, not `url`: a schemeless `url` still yields a clickable normalized href,
  // but `new URL(url)` throws on it, so the disclosure would silently fail open. Likewise compare
  // `textToDisplay` rather than `displayText`, or opting in without `displayText` discloses nothing
  // while the raw `url` is what the reader sees.
  const undisclosedHost = shouldShowDestinationHost && href ? getUndisclosedHost(href, textToDisplay) : null;

  useEffect(() => {
    // Simple heuristic: if display text is longer than estimated character count, show controls
    // This is more reliable than trying to measure DOM elements
    const estimatedLines = Math.ceil(textToDisplay.length / charsPerLine);
    setShouldShowControls(estimatedLines > maxLines);

    // Reset expanded state when URL changes
    setIsExpanded(false);
  }, [textToDisplay, maxLines, charsPerLine]);

  // Line-based truncation styles using Panda CSS
  const truncationStyles = css.raw({
    lineClamp: maxLines,
  });

  const defaultContainerCss = {
    marginBottom: 'default',
    padding: 'default',
    backgroundColor: 'bg.action.neutral.alt.default',
    borderRadius: 'md',
    width: { base: '274px', md: '470px' },
    maxWidth: { base: 'calc(100vw - 64px)', md: 'min(470px, calc(100vw - 96px))' },
    minWidth: 0,
    maxHeight: isExpanded ? '200px' : 'auto',
    overflow: isExpanded ? 'auto' : 'visible',
    ...containerCss,
  } as const;

  const defaultUrlButtonCss = {
    textDecoration: 'underline',
    wordBreak: 'break-all' as const,
    whiteSpace: 'pre-wrap' as const,
    textAlign: 'center' as const,
    width: '100%',
    minWidth: 0,
    overflowWrap: 'break-word' as const,
    ...urlButtonCss,
  };

  return (
    <Box css={defaultContainerCss}>
      {href ? (
        <TextButton
          asChild
          tone="brand"
          css={{
            ...defaultUrlButtonCss,
            ...(!isExpanded && shouldShowControls && truncationStyles),
          }}
        >
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            // Tabbing straight to the link would otherwise announce only the spoofed text.
            aria-describedby={undisclosedHost ? destinationHostId : undefined}
          >
            {textToDisplay}
          </a>
        </TextButton>
      ) : (
        <>
          <Box
            css={{
              ...defaultUrlButtonCss,
              textDecoration: 'none',
              ...(!isExpanded && shouldShowControls && truncationStyles),
            }}
          >
            {textToDisplay}
          </Box>
          <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', marginTop: 'tighter' }}>
            This link can’t be opened.
          </Paragraph>
        </>
      )}
      {undisclosedHost && (
        <Paragraph
          id={destinationHostId}
          textStyle="body-xs"
          css={{ marginTop: 'tighter', textAlign: 'center', wordBreak: 'break-all' }}
        >
          {'This link opens '}
          <strong>{undisclosedHost}</strong>
        </Paragraph>
      )}
      {shouldShowControls && (
        <Box css={{ marginTop: 'tighter', textAlign: 'center' }}>
          <TextButton tone="neutral" onClick={() => setIsExpanded(!isExpanded)} css={{ fontSize: 'sm' }}>
            {isExpanded ? 'Show less' : 'Show full URL'}
          </TextButton>
        </Box>
      )}
    </Box>
  );
}

export default TruncatedUrlDisplay;
